import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Users, Utensils, Star } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Preissektion der Website
 * Zeigt die verschiedenen Preismodelle für den Veranstaltungsraum
 * Verwendet animierte Karten für eine ansprechende Darstellung
 */
export default function PricingSection() {
  /**
   * Preismodell für Wochentage
   * Gilt für Montag bis Freitag von 8:00 bis 16:00 Uhr
   */
  const weekdayPricing = {
    title: "Wochentage",
    subtitle: "Montag 8:00 - Freitag 8:00",
    price: "50€",
    period: "pro 4h Block",
    features: [
      "Flexible Zeiträume",
      "Abrechnung pro angefangene 4h"
    ],
    icon: Clock,
    color: "#0D5C63"
  };

  /**
   * Preismodell für Wochenenden (Vereine)
   * Gilt für Freitag ab 16:00 bis Montag 8:00 Uhr
   * Ermäßigter Preis für lokale Vereine
   */
  const weekendClubPricing = {
    title: "Wochenende - Verein",
    subtitle: "Freitag 8:00 - Montag 8:00", 
    price: "280€",
    period: "Pauschalpreis",
    features: [
      "Sonderpreis für ortsansässige Vereine",
      "Komplettes Wochenende"
    ],
    icon: Users,
    color: "#2D5A2D"
  };

  /**
   * Preismodell für Wochenenden (Privat/Unternehmen)
   * Gilt für Freitag ab 16:00 bis Montag 8:00 Uhr
   * Standardpreis für Privatpersonen und Unternehmen
   */
  const weekendPrivatePricing = {
    title: "Wochenende - Privat",
    subtitle: "Freitag 8:00 - Montag 8:00",
    price: "380€", 
    period: "Pauschalpreis",
    features: [
      "Privatpersonen, Unternehmen & Firmen",
      "Komplettes Wochenende"
    ],
    icon: Star,
    color: "#333333"
  };

  // Alle Preismodelle in einem Array für einfache Iteration
  const pricingCards = [weekdayPricing, weekendClubPricing, weekendPrivatePricing];

  return (
    <section className="py-20 bg-gray-50" aria-labelledby="pricing-heading">
      <div className="max-w-7xl mx-auto px-4">
        {/* Überschrift und Beschreibung */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 id="pricing-heading" className="text-4xl md:text-5xl font-light mb-6 text-[#0D5C63]">
            Transparente <span className="font-semibold">Preise</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Klare und faire Preisgestaltung für alle Veranstaltungen.
            Besondere Konditionen für lokale Vereine und Organisationen.
          </p>
        </motion.div>

        {/* Preiskarten in einem Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {pricingCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 bg-white" aria-labelledby={`pricing-card-${index}`}>
                {/* Karten-Header mit Icon, Titel und Preis */}
                <CardHeader className="pb-4" style={{ backgroundColor: card.color, color: 'white' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-white/30 rounded-lg">
                      <card.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <CardTitle id={`pricing-card-${index}`} className="text-xl font-semibold text-white">{card.title}</CardTitle>
                      <p className="text-white text-sm font-medium">{card.subtitle}</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-1 text-white">{card.price}</div>
                    <div className="text-white text-sm font-medium">{card.period}</div>
                  </div>
                </CardHeader>
                
                {/* Karten-Inhalt mit Features */}
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {card.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: card.color }} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Zusatzinformationen */}
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-md">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-sm font-medium text-gray-700">
              Geschirrnutzung im Preis enthalten
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}